import { type ReactNode, useContext, useMemo } from "react";

import { BTLinkRoutingProvider, type IBTLinkRouting } from "@btnet/core-components";

import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import { routes } from "utilities/routes";
import { formatRouteForNewTab } from "utilities/url/url";

/**
 * Bridges the app's routing subsystems into core BTLink's ambient
 * {@link BTLinkRoutingProvider}, so links migrated off the `btWrappers/BTLink`
 * shim onto `@btnet/core-components` need only change their import path — they
 * no longer pass a `routing` prop.
 *
 * `isSpa` is read from {@link SpaInfoContext} rather than hardcoded because it
 * varies by position in the tree (it's established per-layout). Render this just
 * inside each `SpaInfoContext.Provider` so the SPA value it captures is the
 * layout's, not the context default.
 *
 * Analytics is not wired here — core BTLink self-reports link clicks through BDS
 * tracking (`trackBdsEvent`).
 */
export function BTLinkSpaRoutingProvider({ children }: { children: ReactNode }) {
    const spaInfoContext = useContext(SpaInfoContext);

    const routing = useMemo<IBTLinkRouting>(
        () => ({
            isSpa: spaInfoContext?.isSpa ?? false,
            appBase: routes.appBase,
            formatRouteForNewTab,
        }),
        [spaInfoContext?.isSpa],
    );

    return <BTLinkRoutingProvider value={routing}>{children}</BTLinkRoutingProvider>;
}
